import { useProfile } from "@/context/useProfileProps";
import { customTwMerge as twMerge } from "@/helpers/customTwMerge";
import { handleFooterBtnLoading } from "@/types/common";
import React, { useEffect, useRef, useState } from "react";

import ProfileModal from "@/components/common/profileModal";
import LoadingSummary from "@/components/profile/profileSummary/components/loadingSummary";
import SummaryBtn from "@/components/profile/profileSummary/components/summaryBtn";
import SummaryOptions from "@/components/profile/profileSummary/components/summaryOptions";
import ProfileSummaryEditModal from "@/components/profile/profileSummary/edit";
import {
  useGetAiSummary,
  useGetAiSummaryId,
} from "@/components/profile/profileSummary/query";
import {
  AiSummaryActionType,
  AiSummaryResponse,
  GeneratedSummaryId,
  ProfileType,
  SummaryType,
} from "@/components/profile/profileSummary/types";

type RenderModalProps = {
  isEdit: string;
  closeEdit: () => void;
  prefilledData?: ProfileType;
  prefilledAiSummaryData: AiSummaryResponse | null;
  isCalledFromOutside?: boolean;
  formRef?: React.RefObject<HTMLFormElement>;
  handleFooterBtnLoading?: handleFooterBtnLoading;
};

const RenderModal = ({
  isEdit,
  closeEdit,
  prefilledData,
  prefilledAiSummaryData,
  isCalledFromOutside = false,
  formRef,
  handleFooterBtnLoading,
}: RenderModalProps) => {
  const {
    isMobile,
    translation: { profileSummary, errorMessages },
    showToast,
  } = useProfile();
  const [generatedSummaries, setGeneratedSummaries] = useState<SummaryType[]>(
    [],
  );
  const [isAiSummaryLoading, setIsAiSummaryLoading] = useState(false); // to show ai summary loading modal
  const [showAiSummaryModal, setShowAiSummaryModal] = useState(false); // to hide edit modal
  const [isAiSummaryFetched, setIsAiSummaryFetched] = useState(false); // to show ai summary options modal
  const [prefilledProfileSummary, setPrefilledProfileSummary] =
    useState<ProfileType | null>(null);
  const [aiSummaryData, setAiSummaryData] = useState<AiSummaryResponse | null>(
    null,
  );
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const updatedformRef = formRef ?? useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (prefilledData) {
      setPrefilledProfileSummary(prefilledData);
    }
  }, [prefilledData]);

  useEffect(() => {
    if (prefilledAiSummaryData) {
      filterGeneratedSummaries(prefilledAiSummaryData);
    }
  }, [prefilledAiSummaryData]);

  // open modal according to the type
  useEffect(() => {
    if (isEdit === "summary" && prefilledData) {
      handleAiBtnClick("generate", prefilledData.title);
    } else {
      setShowAiSummaryModal(false);
    }
  }, [isEdit, prefilledData]);

  // init ai summary data
  const filterGeneratedSummaries = (aiSummaryResp: AiSummaryResponse) => {
    setAiSummaryData(aiSummaryResp);
    const generatedSummaries = aiSummaryResp?.generatedSummary
      ? aiSummaryResp.generatedSummary.filter(
          (summary) => !summary.usedInProfile,
        )
      : [];
    if (prefilledData) {
      generatedSummaries.unshift({
        id: "current",
        usedInProfile: true,
        summary: prefilledData.title,
      });
    }
    setGeneratedSummaries(generatedSummaries);
  };

  const { mutate: getAiSummaryId } = useGetAiSummaryId();
  const { mutate: getAiSummary } = useGetAiSummary();

  const handleAiSummaryError = () => {
    closeAiSummary();
    showToast({
      show: true,
      message: "Unable to generate ai summary",
      type: "error",
    });
  };

  const handleGenerateSummary = (prompt: string) => {
    setIsAiSummaryLoading(true);
    setShowAiSummaryModal(true);
    getAiSummaryId(
      {
        data: {
          summary: prompt,
        },
      },
      {
        onSuccess: (data) => {
          setTimeout(() => {
            getGeneratedAiSummary(data, 1);
          }, 5000);
        },
        onError: (error) => {
          handleAiSummaryError();
        },
      },
    );
  };

  const getGeneratedAiSummary = async (
    data: GeneratedSummaryId,
    count: number,
  ) => {
    getAiSummary(
      {
        params: {
          requestId: data.requestId,
        },
      },
      {
        onSuccess: (res) => {
          if (!res.generatedSummary && count < 5) {
            count++;
            const delayTime =
              count === 2 ? 3000 : count === 3 ? 2000 : count === 4 ? 3000 : 0;
            if (delayTime) {
              setTimeout(() => {
                getGeneratedAiSummary(data, count);
              }, delayTime);
            } else {
              handleAiSummaryError();
            }
          } else {
            filterGeneratedSummaries(res);
            setIsAiSummaryFetched(true);
            setTimeout(() => {
              setIsAiSummaryLoading(false);
            }, 700);
            showToast({
              show: true,
              message: "AI summary generated successfully",
              type: "success",
            });
          }
        },
        onError: (error) => {
          handleAiSummaryError();
        },
      },
    );
  };

  const closeAiSummary = () => {
    setShowAiSummaryModal(false);
    setIsAiSummaryFetched(false);
    setIsAiSummaryLoading(false);
  };

  const handleInsertedSummary = (index: number) => {
    setTimeout(() => {
      closeAiSummary();
    }, 500);
    // insert summary in profile edit
    setPrefilledProfileSummary((prev: any) => ({
      ...prev,
      title: generatedSummaries[index].summary,
    }));
    // set inserted summary
    setGeneratedSummaries((prev) =>
      prev.map((summary, i) => ({
        ...summary,
        usedInProfile: i === index,
      })),
    );
  };

  const handleAiBtnClick = (type: AiSummaryActionType, prompt = "") => {
    if (type === "generate") {
      handleGenerateSummary(prompt);
    } else {
      setShowAiSummaryModal(true);
      setIsAiSummaryFetched(true);
    }
  };

  const handleFormSubmit = () => {
    if (updatedformRef.current) {
      updatedformRef.current.requestSubmit();
    }
  };
  const renderViewOption = aiSummaryData?.isUserEligible &&
    !showAiSummaryModal &&
    aiSummaryData?.generatedSummary &&
    aiSummaryData.generatedSummary.length > 0 && (
      <SummaryBtn type="view" handleAiBtnClick={handleAiBtnClick} />
    );

  const handleSaveBtnLoading = (isLoading = false) => {
    if (isCalledFromOutside && handleFooterBtnLoading) {
      handleFooterBtnLoading({
        isSaveLoading: isLoading,
      });

      return;
    }
    setIsSaveLoading(isLoading);
  };

  const modalContent = (
    <>
      {!showAiSummaryModal ? (
        <ProfileSummaryEditModal
          closeEdit={closeEdit}
          prefilledData={prefilledProfileSummary}
          handleAiBtnClick={handleAiBtnClick}
          aiSummaryData={aiSummaryData}
          summaryOptions={generatedSummaries}
          handleSaveBtnLoading={handleSaveBtnLoading}
          ref={updatedformRef}
        />
      ) : (
        <>
          {isAiSummaryLoading && (
            <LoadingSummary isStartToFade={isAiSummaryFetched} />
          )}
          {isAiSummaryFetched && (
            <SummaryOptions
              summaryOptions={generatedSummaries}
              handleInsertedSummary={handleInsertedSummary}
            />
          )}
        </>
      )}
    </>
  );
  return !isCalledFromOutside ? (
    <ProfileModal
      heading={
        showAiSummaryModal && !isAiSummaryLoading
          ? profileSummary.chooseOption
          : profileSummary.title
      }
      showModal={!!isEdit}
      closeModal={closeEdit}
      isMobile={isMobile}
      showCloseButton={renderViewOption && isMobile ? false : true}
      screenAlignment={isMobile ? "bottom-sheet" : "center"}
      showHandleBar={true}
      footerPrimaryBtnLabel={!showAiSummaryModal ? "Save" : ""}
      footerPrimaryBtnHandler={handleFormSubmit}
      backBtnhandler={
        showAiSummaryModal && !isAiSummaryLoading ? closeAiSummary : null
      }
      customClass={twMerge(
        isMobile ? "min-h-[60vh]" : "h-[50vh]",
        isAiSummaryLoading && !isMobile ? "rounded-b-2xl" : "",
      )}
      rightBtn={renderViewOption}
      footerPrimaryBtnIsLoading={isSaveLoading}
    >
      {modalContent}
    </ProfileModal>
  ) : (
    modalContent
  );
};

export default RenderModal;
